import api from './api';
import type {
  ApiResponse,
  TargetPeriod,
  AgentProgress,
  LeaderboardEntry,
  CreateTargetPeriodData,
  UpdateTargetPeriodData,
  SetTargetTiersData,
  MetricOption,
} from '@/types';

// Admin APIs

export interface TargetPeriodsResponse {
  periods: TargetPeriod[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

// Get all target periods (admin)
export const getTargetPeriods = async (): Promise<TargetPeriodsResponse> => {
  const response = await api.get<ApiResponse<TargetPeriodsResponse>>('/admin/targets');
  return response.data.data;
};

// Get available metrics (admin)
export const getTargetMetrics = async (): Promise<MetricOption[]> => {
  const response = await api.get<ApiResponse<{ metrics: MetricOption[] }>>('/admin/targets/metrics');
  return response.data.data.metrics;
};

// Get a specific target period (admin)
export const getTargetPeriod = async (periodId: string): Promise<TargetPeriod> => {
  const response = await api.get<ApiResponse<TargetPeriod>>(`/admin/targets/${periodId}`);
  return response.data.data;
};

// Create a new target period (admin)
export const createTargetPeriod = async (data: CreateTargetPeriodData): Promise<TargetPeriod> => {
  const response = await api.post<ApiResponse<TargetPeriod>>('/admin/targets', data);
  return response.data.data;
};

// Update a target period (admin)
export const updateTargetPeriod = async (
  periodId: string,
  data: UpdateTargetPeriodData
): Promise<TargetPeriod> => {
  const response = await api.put<ApiResponse<TargetPeriod>>(`/admin/targets/${periodId}`, data);
  return response.data.data;
};

// Delete a target period (admin)
export const deleteTargetPeriod = async (periodId: string): Promise<void> => {
  await api.delete(`/admin/targets/${periodId}`);
};

// Set tiers for a target period (admin)
export const setTargetTiers = async (
  periodId: string,
  data: SetTargetTiersData
): Promise<TargetPeriod> => {
  const response = await api.post<ApiResponse<TargetPeriod>>(`/admin/targets/${periodId}/tiers`, data);
  return response.data.data;
};

// Get progress for all agents in a period (admin)
export const getTargetProgress = async (periodId: string): Promise<AgentProgress[]> => {
  const response = await api.get<ApiResponse<AgentProgress[]>>(`/admin/targets/${periodId}/progress`);
  return response.data.data;
};

// Lock a target period (admin)
export const lockTargetPeriod = async (periodId: string): Promise<TargetPeriod> => {
  const response = await api.post<ApiResponse<TargetPeriod>>(`/admin/targets/${periodId}/lock`);
  return response.data.data;
};

// Agent APIs

export interface MyProgressResponse {
  has_active_period: boolean;
  message?: string;
  period?: TargetPeriod;
  progress?: AgentProgress;
}

export interface LeaderboardResponse {
  has_active_period: boolean;
  message?: string;
  period?: TargetPeriod;
  leaderboard?: LeaderboardEntry[];
}

export interface ProgressHistoryResponse {
  history: AgentProgress[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

// Get current period progress for authenticated user
export const getMyProgress = async (): Promise<MyProgressResponse> => {
  const response = await api.get<ApiResponse<MyProgressResponse>>('/my-progress');
  return response.data.data;
};

// Get progress history for authenticated user
export const getMyProgressHistory = async (): Promise<ProgressHistoryResponse> => {
  const response = await api.get<ApiResponse<ProgressHistoryResponse>>('/my-progress/history');
  return response.data.data;
};

// Manually refresh progress for authenticated user
export const refreshMyProgress = async (): Promise<AgentProgress> => {
  const response = await api.post<ApiResponse<AgentProgress>>('/my-progress/refresh');
  return response.data.data;
};

// Get current period leaderboard
export const getLeaderboard = async (): Promise<LeaderboardResponse> => {
  const response = await api.get<ApiResponse<LeaderboardResponse>>('/leaderboard');
  return response.data.data;
};

// Get leaderboard for a specific period
export const getLeaderboardForPeriod = async (periodId: string): Promise<LeaderboardResponse> => {
  const response = await api.get<ApiResponse<LeaderboardResponse>>(`/leaderboard/${periodId}`);
  return response.data.data;
};
